import { Box } from "@mui/material";
import { DotLegalAlert, DotLegalCheckbox, DotLegalDatePicker, DotLegalSelect, DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import SegmentedControl from "@common/components/segmentedControl/SegmentedControl";
import RelativeDateConfig from "@common/components/relativeDateConfig/RelativeDateConfig";
import ActivityFormBox from "./ActivityFormBox";
import { ActivityScheduleSectionProps, useActivityScheduleSection } from "./ActivityScheduleSection.hooks";
import { useActivityFormStyles } from "./ActivityForm.styles";

// Lines the checkbox's label up with the labels of the fields beside it, standing in as the end date's label.
const endDateCheckboxMargin = { marginLeft: 0, marginTop: 0.625, marginBottom: -0.875 };

function ActivityScheduleSection(props: ActivityScheduleSectionProps) {
    const { translateString, getDateTimePickerLanguage } = useTranslation();
    const styles = useActivityFormStyles();
    const {
        isRecurring,
        canBeOneTime,
        frequency,
        frequencyOptions,
        intervalTypeOptions,
        scheduleSummary,
        intervalErrorText,
        showStartDateError,
        showEndDateError,
        notificationPeriodErrorText,
        onFrequencySelect,
        onIntervalChange,
        onIntervalTypeChange,
        onStartDateChange,
        onEndDateChange,
        onDisplayEndDateChange,
        onNotificationPeriodInDaysChange,
        onToggleRelativeStartDate,
        onToggleRelativeEndDate,
        startDateRelativeHandlers,
        endDateRelativeHandlers,
    } = useActivityScheduleSection(props);
    const model = props.model.schedule;
    const startDateLabel = isRecurring ? translateString("startDate") : translateString("deadline");

    return (
        <ActivityFormBox title={translateString("activityFormSchedule")} startsWithFieldLabel={!canBeOneTime}>
            {canBeOneTime && (
                <Box sx={styles.frequency}>
                    <SegmentedControl
                        ariaLabel={translateString("frequency")}
                        options={frequencyOptions}
                        selected={frequency}
                        onSelect={onFrequencySelect}
                        disabled={props.readOnly}
                        fullWidth
                    />
                </Box>
            )}

            <Box sx={styles.scheduleColumns}>
                {isRecurring && (
                    <Box sx={styles.fullRow}>
                        <Box sx={styles.interval}>
                            <DotLegalTextField
                                label={translateString("interval")}
                                useFixedLabel
                                onlyNumber={"integer"}
                                startAdorment={translateString("every")}
                                value={model.interval?.toString() ?? null}
                                onChange={onIntervalChange}
                                debounce={false}
                                disabled={props.readOnly}
                                hasError={!!intervalErrorText}
                                noMargin
                            />
                            <DotLegalSelect
                                label={""}
                                options={intervalTypeOptions}
                                selectedItem={model.recurrenceIntervalType?.toString() ?? ""}
                                onChange={onIntervalTypeChange}
                                placeholder={translateString("period")}
                                noOptionsLabel={translateString("noOptions")}
                                disableClearable
                                disabled={props.readOnly}
                                useFixedLabel
                                noMargin
                            />
                        </Box>
                        {intervalErrorText && <Box sx={styles.error}>{intervalErrorText}</Box>}
                    </Box>
                )}

                <Box>
                    {!model.useRelativeStartDate && (
                        <DotLegalDatePicker
                            label={startDateLabel}
                            value={model.startDate}
                            onChange={onStartDateChange}
                            error={showStartDateError}
                            helperMessage={showStartDateError ? translateString("fieldRequired") : undefined}
                            disabled={props.readOnly}
                            language={getDateTimePickerLanguage()}
                            useFixedLabel
                        />
                    )}
                    {props.contractInfo && (
                        <RelativeDateConfig
                            label={startDateLabel}
                            config={model.startDateRelativeConfig}
                            calculatedDate={model.calculateAbsoluteStartDate()}
                            useRelativeDate={model.useRelativeStartDate}
                            linkLabel={translateString("useRelativeStartDate")}
                            closeLabel={translateString("closeRelativeStartDate")}
                            contractStartDate={props.contractInfo.startDate}
                            contractEndDate={props.contractInfo.endDate}
                            error={showStartDateError}
                            helperMessage={translateString("fieldRequired")}
                            onToggleRelativeDate={onToggleRelativeStartDate}
                            {...startDateRelativeHandlers}
                            readOnly={props.readOnly}
                        />
                    )}
                </Box>

                {isRecurring && (
                    <Box>
                        <Box sx={styles.endDateCheckbox}>
                            <DotLegalCheckbox
                                label={
                                    <Box component="span" sx={styles.endDateCheckboxLabel}>
                                        {translateString("endDate")}
                                    </Box>
                                }
                                checked={model.displayEndDate}
                                onChange={onDisplayEndDateChange}
                                disabled={props.readOnly}
                                margin={endDateCheckboxMargin}
                            />
                        </Box>
                        {!model.displayEndDate && <Box sx={styles.endDatePlaceholder}>{translateString("obligationRepeatsUntilStopped")}</Box>}
                        {model.displayEndDate && !model.useRelativeEndDate && (
                            <DotLegalDatePicker
                                noMargin
                                label={""}
                                value={model.endDate}
                                onChange={onEndDateChange}
                                error={showEndDateError}
                                helperMessage={showEndDateError ? translateString("cannotCreateActivitiesWithEndBeforeStart") : undefined}
                                disabled={props.readOnly}
                                language={getDateTimePickerLanguage()}
                            />
                        )}
                        {model.displayEndDate && props.contractInfo && (
                            <RelativeDateConfig
                                label={""}
                                noMargin
                                config={model.endDateRelativeConfig}
                                calculatedDate={model.calculateAbsoluteEndDate()}
                                useRelativeDate={model.useRelativeEndDate}
                                linkLabel={translateString("useRelativeEndDate")}
                                closeLabel={translateString("closeRelativeEndDate")}
                                contractStartDate={props.contractInfo.startDate}
                                contractEndDate={props.contractInfo.endDate}
                                error={showEndDateError}
                                helperMessage={translateString("cannotCreateActivitiesWithEndBeforeStart")}
                                onToggleRelativeDate={onToggleRelativeEndDate}
                                {...endDateRelativeHandlers}
                                readOnly={props.readOnly}
                            />
                        )}
                    </Box>
                )}

                <Box sx={[styles.fullRow, styles.reminder]}>
                    <DotLegalTextField
                        label={translateString("obligationReminder")}
                        toolTipText={translateString("obligationReminderTooltip")}
                        onlyNumber={"integer"}
                        endAdorment={translateString("obligationDaysBeforeDeadline")}
                        value={model.notificationPeriodInDays?.toString() ?? null}
                        onChange={onNotificationPeriodInDaysChange}
                        debounce={false}
                        disabled={props.readOnly}
                        errorText={notificationPeriodErrorText}
                        useFixedLabel
                    />
                </Box>
            </Box>

            {model.hasRelativeDateFromTemplateButCantDisplay && props.contractInfo && (
                <DotLegalAlert warningText={translateString("obligationTemplateRelativeDateNotOnContract")} />
            )}

            {scheduleSummary && <Box sx={styles.hint}>{scheduleSummary}</Box>}
        </ActivityFormBox>
    );
}

export default ActivityScheduleSection;
